import { type MouseEvent, useEffect, useMemo, type VoidFunctionComponent } from 'react';
import { useDispatch } from 'react-redux';

import { useParams } from '../../../contexts/RouterContext';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useRevisionIssueTypeSummary } from '../hooks/useRevisionIssueTypeSummary';
import { useRevisionScanIdQueryParam } from '../hooks/useRevisionScanIdQueryParam';
import IssueListItem from '../issues/IssueListItem';
import type { IssueType } from '../../../../shared/issuesV2';
import type { ProjectDashboardProject, ProjectDashboardRevision, RevisionRouteParams } from '../projectDashboardTypes';

import ProjectChecksPassedPanel from './ProjectChecksPassedPanel';
import ProjectHasIssuesPanel from './ProjectHasIssuesPanel';
import ProjectPendingScanPanel from './ProjectPendingScanPanel';
import ProjectSummaryIssuesSummaryHeader from './ProjectSummaryIssuesSummaryHeader';

export type ScanStatus = 'PENDING' | 'SUCCEEDED' | 'FAILED';

type Props = {
  project: ProjectDashboardProject;
  revision: ProjectDashboardRevision;
  onScanIssues(event: MouseEvent<HTMLElement>): void;
};

const ProjectSummaryIssues: VoidFunctionComponent<Props> = ({
  project,
  revision,
  onScanIssues,
}) => {
  const dispatch = useDispatch();
  const params = useParams<RevisionRouteParams>();
  const { revisionScanId, setRevisionScanId } = useRevisionScanIdQueryParam();

  // Fetch issue types summary using React Query hook
  const {
    isLoading: isLoadingIssueTypesSummary,
    issueTypesSummary,
    totalIssueCount,
  } = useRevisionIssueTypeSummary({
    revisionLocator: revision.data?.locator,
    latestRevisionScanId: revision.data?.latestRevisionScanId,
  });

  // Initialize query param on first load if missing
  // This ensures the revisionScanId is always present in the URL, making the data flow explicit
  useEffect(() => {
    if (!revisionScanId && revision.data?.latestRevisionScanId) {
      const scanId = revision.data.latestRevisionScanId;
      dispatch(IssuesV2Store.actions.setScanId(scanId));
      setRevisionScanId(scanId);
    }
  }, [revisionScanId, revision.data?.latestRevisionScanId, dispatch, setRevisionScanId]);

  const isScanned = revision.data?.meta?.[0]?.last_scan ?? false;
  const issueCount = totalIssueCount ?? 0;

  // Render issue cards from React Query hook data (memoized to avoid unnecessary re-renders)
  const renderedIssues = useMemo(
    () =>
      Object.entries(issueTypesSummary ?? {})
        .filter(([, count]) => count > 0)
        .sort(([, countA], [, countB]) => countB - countA)
        .map(([type, count]) => (
          <IssueListItem
            key={type}
            issueType={type as IssueType}
            count={count}
            projectOrgId={project.data?.organizationId}
          />
        )),
    [issueTypesSummary, project.data?.organizationId],
  );

  let projectScanStatus: ScanStatus;
  if (!isScanned || isLoadingIssueTypesSummary) {
    projectScanStatus = 'PENDING';
  } else if (issueCount > 0) {
    projectScanStatus = 'FAILED';
  } else {
    projectScanStatus = 'SUCCEEDED';
  }

  return (
    <>
      <ProjectSummaryIssuesSummaryHeader
        revision={revision}
        project={project}
        issueCount={issueCount}
        status={projectScanStatus}
        isLoading={isLoadingIssueTypesSummary}
        onScanIssues={onScanIssues}
      />
      {projectScanStatus === 'PENDING' && !isLoadingIssueTypesSummary &&
        <ProjectPendingScanPanel
          params={params}
          revision={revision}
          project={project}
          onScanIssues={onScanIssues}
        />}
      {projectScanStatus === 'FAILED' &&
        <ProjectHasIssuesPanel
          renderedIssues={renderedIssues}
        />}
      {projectScanStatus === 'SUCCEEDED' &&
        <ProjectChecksPassedPanel
          params={params}
          revision={revision}
          project={project}
        />}
    </>
  );
};

export default ProjectSummaryIssues;
